import { TimeRangeFilter } from '@/components/admin/TimeRangeFilter';
import { Button } from '@/components/ui/button';
import { FileDown, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * O que faz: Exibe o cabeçalho visual do Dashboard com título, ações, filtro de período e atualização manual.
 * Onde usa: Componente Dashboard principal.
 * Conexões: Recebe callbacks para filtro, atualização, exportação PDF e ações externas como relatórios de IA.
 * Tabelas: Não acessa banco diretamente; apenas aciona funções recebidas por props.
 * Data: 11/06/2026 (ações na linha do período em 05/10/2026)
 */

interface DashboardHeaderProps {
  onFilterChange: (startDate: Date | null, endDate: Date | null, filterName: string) => void;
  currentFilter: string;
  onRefreshClick?: () => void;
  isRefreshing?: boolean;
  onExportClick?: () => void;
  clinicName?: string | null;
  actions?: ReactNode;
  // Ações da linha do período (ex.: "Analisar conversas"), alinhadas à direita.
  acoesPeriodo?: ReactNode;
}

export const DashboardHeader = ({
  onFilterChange,
  currentFilter,
  onRefreshClick,
  isRefreshing = false,
  onExportClick,
  clinicName,
  actions,
  acoesPeriodo
}: DashboardHeaderProps) => {
  return (
    <div className="space-y-4">
      {/* Linha 1: título + ações */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">Dashboard</h1>
            {clinicName && (
              <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#8fb83a]" aria-hidden="true"></span>
                <span className="truncate">{clinicName}</span>
              </span>
            )}
          </div>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Acompanhe as métricas e a performance da sua clínica
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {actions}
          {onExportClick && (
            <Button
              onClick={onExportClick}
              variant="outline"
              size="sm"
              className="h-10 cursor-pointer items-center gap-2 rounded-full border-input bg-card px-4 text-[13px] font-semibold text-foreground transition-colors duration-200 hover:border-[#c4cdbb] hover:bg-accent/60 hover:text-accent-foreground"
            >
              <FileDown className="h-4 w-4" aria-hidden="true" />
              Exportar PDF
            </Button>
          )}
        </div>
      </div>

      {/* Linha 2: período + atualização */}
      <div className="flex flex-wrap items-center gap-2">
        <TimeRangeFilter
          onFilterChange={onFilterChange}
          currentFilter={currentFilter}
        />

        {/* Permite atualizar as métricas manualmente sem trocar o período */}
        {onRefreshClick && (
          <Button
            onClick={onRefreshClick}
            variant="ghost"
            size="sm"
            disabled={isRefreshing}
            className="h-9 cursor-pointer items-center gap-2 rounded-full px-3.5 text-[13px] font-medium text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {isRefreshing ? 'Atualizando...' : 'Atualizar'}
          </Button>
        )}

        {acoesPeriodo && <div className="ml-auto flex items-center gap-2">{acoesPeriodo}</div>}
      </div>
    </div>
  );
};
